/**
 * Error Translation Utilities
 *
 * Translates error codes to user-friendly messages using i18n.
 * Falls back to the original error message if translation is not found.
 */

import i18n from "i18next";
import {
  isApiError,
  getErrorCode,
  getErrorMessage,
  getErrorDetails,
} from "./types";

/**
 * Translates an error to a user-friendly message.
 * Uses i18n translation keys based on error code.
 *
 * Translation key pattern: errors.{ERROR_CODE}.message
 *
 * @param error - The error to translate
 * @returns Translated error message
 */
export function translateError(error: unknown): string {
  const code = getErrorCode(error);
  const details = getErrorDetails(error);

  if (!code) {
    return getErrorMessage(error);
  }

  // Try to translate with the error code
  const translationKey = `errors.${code}.message`;
  const translated = i18n.t(translationKey, {
    ...details,
    defaultValue: "", // Return empty if not found
  });

  // If translation exists and is not empty, use it
  if (translated && translated !== translationKey) {
    return translated;
  }

  // Fallback to original message
  return getErrorMessage(error);
}

/**
 * Translates an error to a user-friendly title.
 * Uses i18n translation keys based on error code.
 *
 * Translation key pattern: errors.{ERROR_CODE}.title
 *
 * @param error - The error to translate
 * @returns Translated error title
 */
export function translateErrorTitle(error: unknown): string {
  const code = getErrorCode(error);

  if (!code) {
    return error instanceof Error ? error.name : "Error";
  }

  // Try to translate with the error code
  const translationKey = `errors.${code}.title`;
  const translated = i18n.t(translationKey, {
    defaultValue: "", // Return empty if not found
  });

  // If translation exists and is not empty, use it
  if (translated && translated !== translationKey) {
    return translated;
  }

  // Fallback to error name or generic title
  if (isApiError(error)) {
    return error.name || "Error";
  }

  return "Error";
}

/**
 * Get a short action label for the error (e.g., "Try again", "Contact support")
 *
 * Translation key pattern: errors.{ERROR_CODE}.action
 *
 * @param error - The error to get action for
 * @returns Action label or undefined if no specific action
 */
export function getErrorAction(error: unknown): string | undefined {
  const code = getErrorCode(error);

  if (!code) {
    return undefined;
  }

  const translationKey = `errors.${code}.action`;
  const translated = i18n.t(translationKey, {
    defaultValue: "",
  });

  if (translated && translated !== translationKey) {
    return translated;
  }

  return undefined;
}
